import { parseBlobRequestError, parseRequestError } from '@/common/utilities'
import { zodErrorService } from '@/lib/zod-error-service'
import {
  ImportProvidersResponse,
  ImportProvidersResponseSchema,
  ProviderAcceptRejectResponse,
  ProviderAcceptRejectResponseSchema,
  ProviderItemCategoryList,
  ProviderItemCategoryListSchema,
  SendProviderMessageResponse,
  SendProviderMessageResponseSchema,
  UpdateSchoolApplicationPayload,
} from '@/queries/providers/schema.ts'
import {
  AccessTokenPreviewResponseSchema,
  ProviderPreviewSettingsSchema,
  ProviderSchema,
} from '@/schemas/provider.schema'
import axios from 'axios'
import { z } from 'zod'

const ProvidersPaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(ProviderSchema),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.union([
      z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
      z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
    ])
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})

export type PaginatedProviders = z.infer<typeof ProvidersPaginatedSchema>

type Provider = z.infer<typeof ProviderSchema>

export type ProviderSortBy =
  | 'id'
  | 'provider_status'
  | 'name'
  | 'category'
  | 'state_name'
  | 'status'
  | 'bank_account_status'
  | 'submitted_at'

export type ProviderList = Provider[]

export interface ExportProvidersParams {
  programId: number
  search?: string
  statusIn?: string
}

export const getPaginatedProviders = async function (
  program_id: number,
  currentPage: number,
  recordsPerPage: number = 20,
  search?: string | undefined,
  sort_by?: ProviderSortBy,
  sort_order?: 'asc' | 'desc',
  status_in?: string
): Promise<PaginatedProviders> {
  try {
    const params: {
      paginate: number
      program_id: number
      per_page: number
      page: number
      search?: string
      sort_by?: string
      sort_order?: string
      status_in?: string
    } = {
      paginate: 1,
      program_id,
      per_page: recordsPerPage,
      page: currentPage,
      ...(search && { search }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
      ...(status_in && { status_in }),
    }

    const response = await axios.get('/provider', {
      params,
    })

    return ProvidersPaginatedSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const exportProviders = async ({
  programId,
  search,
  statusIn,
}: ExportProvidersParams): Promise<Blob> => {
  try {
    const response = await axios.get('/provider/export', {
      responseType: 'blob',
      params: {
        program_id: programId,
        ...(search && { search }),
        ...(statusIn && { status_in: statusIn }),
      },
    })

    return response.data
  } catch (error) {
    throw new Error((await parseBlobRequestError(error)).message)
  }
}

export const getProviderDetail = async (
  program_id: number,
  providerId: string | number
) => {
  try {
    const response = await axios.get(`provider/${providerId}`, {
      params: {
        program_id,
      },
    })

    return response.data
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}
export const getPreviewSettings = async (
  program_id: number,
  providerId: number | string
) => {
  try {
    const response = await axios.post(
      `provider/${providerId}/get_or_create_preview_settings`,
      null,
      {
        params: { program_id },
      }
    )

    const result = zodErrorService.parse(
      ProviderPreviewSettingsSchema,
      response.data
    )
    return result
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

export const getAccessTokenPreview = async (
  programId: number,
  providerId: string
) => {
  try {
    const payload = {
      provider_id: providerId,
      program_id: programId,
    }
    const url = `/marketplace_preview/generate_token`
    const response = await axios.post(url, payload)
    return zodErrorService.parse(
      AccessTokenPreviewResponseSchema,
      response.data
    )
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

export const providerAcceptReject = async (
  providerId: number,
  programId: number,
  status: number,
  reasoningId: number,
  justification: string,
  public_reply: string
): Promise<ProviderAcceptRejectResponse> => {
  try {
    const url = `/provider_accept_reject/${providerId}`

    const payload = {
      program_id: programId,
      status,
      reasoning: reasoningId,
      justification,
      public_reply,
    }

    const response = await axios.put(url, payload)

    return ProviderAcceptRejectResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const sendProviderMessage = async (
  providerId: number,
  message: string
): Promise<SendProviderMessageResponse> => {
  try {
    const url = `/provider/messages`

    const payload = {
      provider_id: providerId,
      message: message,
    }

    const response = await axios.post(url, payload)

    return SendProviderMessageResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const getProviderItemCategories = async (
  provider_id: number,
  program_id: number
): Promise<ProviderItemCategoryList> => {
  try {
    const response = await axios.get(
      `/admin/providers/${provider_id}/item_categories`,
      {
        params: { program_id },
      }
    )

    return ProviderItemCategoryListSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const acceptRejectProviderItem = async (
  providerId: number,
  providerItemId: number,
  programId: number,
  status: 0 | 1 | 2,
  justification?: string
): Promise<ProviderAcceptRejectResponse> => {
  try {
    const url = `/admin/providers/${providerId}/items/${providerItemId}/accept_reject?program_id=${programId}`

    const payload = {
      status,
      ...(status === 2 && justification ? { justification } : {}),
    }

    const response = await axios.put(url, payload)

    return ProviderAcceptRejectResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export async function updateSchoolApplication(
  provider_id: number,
  school_application_id: number,
  program_id: number,
  payload: Partial<UpdateSchoolApplicationPayload>
) {
  return axios.put(
    `/provider/${provider_id}/school_applications/${school_application_id}`,
    payload,
    {
      params: { program_id },
    }
  )
}

export const importProviders = async (
  file: File,
  programId: number
): Promise<ImportProvidersResponse> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('program_id', programId.toString())

    const response = await axios.post('/provider_import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return ImportProvidersResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

const ProviderStatusResponseSchema = z.object({
  provider_id: z.number(),
  status: z.number(),
})

export type ProviderStatusResponse = z.infer<
  typeof ProviderStatusResponseSchema
>

export const updateProviderStatus = async (
  providerId: string,
  status: 0 | 1
): Promise<ProviderStatusResponse> => {
  try {
    const response = await axios.put(
      `/admin/providers/${providerId}/update_status`,
      {
        status,
      }
    )

    return ProviderStatusResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}
